import { ApiError, type Problem, type ValidationErrorResponse } from "./types";

/**
 * Single fetch wrapper used by all API hooks. Reads cross-subdomain
 * cookies via `credentials: include`. Throws ApiError on non-2xx
 * responses.
 *
 * Origin selection:
 *   - In dev: paths are relative; Vite proxies /api → localhost:4099.
 *   - In prod: VITE_API_BASE (e.g. https://api.vor.codes) is prepended
 *     so the static frontend on vor.codes can talk to the API.
 */
export interface FetchOptions {
  readonly method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  readonly body?: unknown;
  readonly query?: Record<string, string | number | undefined>;
  readonly signal?: AbortSignal;
}

/**
 * The runtime API base. Empty string = same-origin / Vite proxy.
 * Set by Vite at build time via `import.meta.env.VITE_API_BASE`.
 * Exported so other helpers (ai-stream, Y.js hooks) can compose URLs
 * against the same base.
 */
function readApiBase(): string {
  if (typeof import.meta === "undefined") return "";
  const env = (import.meta as { env?: Record<string, unknown> }).env;
  if (!env) return "";
  const v = env["VITE_API_BASE"];
  if (typeof v !== "string") return "";
  return v.replace(/\/+$/, "");
}
export const API_BASE: string = readApiBase();

export async function api<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { method = "GET", body, query, signal } = options;

  const rel = path.startsWith("/") ? path : `/${path}`;
  let url = API_BASE + rel;
  if (query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) params.set(k, String(v));
    }
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }

  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed = text ? (safeJson(text) as unknown) : undefined;

  if (!res.ok) {
    const problem: Problem | ValidationErrorResponse =
      parsed && typeof parsed === "object" && "code" in parsed
        ? (parsed as Problem | ValidationErrorResponse)
        : {
            type: "about:blank",
            title: res.statusText || "Request failed",
            status: res.status,
            code: "unknown",
          };
    throw new ApiError(res.status, problem);
  }

  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
